/**
 * MaintenanceSettingsView.js
 *
 * Full-screen settings view for Maintenance module configuration.
 * WBC3 Edition: 7 settings for maintenance management
 *
 * Settings:
 * 1. Visible Categories - Which categories to display
 * 2. Advance Warning Days - Days before due to show "Due Soon" (7-90)
 * 3. Detailed Mode - Show individual equipment in job cards
 * 4. Show Completed Jobs - Toggle visibility of completed jobs
 * 5. Show Custom Jobs - Show user-created maintenance jobs
 * 6. Auto Generate Defects - Create defect when issues found (WBC3 3.5.7)
 * 7. Require Notes for Overdue - Require explanation for overdue jobs (WBC3 Appendix 8)
 *
 * Pattern: Full-screen overlay accessed via gear icon (same as DrillSettingsView)
 */

import React, { useState } from 'react';
import {
  Calendar,
  Eye,
  List,
  CheckSquare,
  Wrench,
  AlertTriangle,
  FileText,
  Info,
  RotateCcw,
  Check,
  Shield
} from 'lucide-react';
import { useMaintenanceConfig, REGULATORY_CATEGORIES } from '../../../../hooks/domain/maintenance/useMaintenanceConfig';
import { MAINTENANCE_CATEGORIES } from '../../../../config/maintenanceRequirements';
// FT-356c: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. Header raw ArrowLeft back button removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

/**
 * Human-readable category names
 */
const CATEGORY_DISPLAY_NAMES = {
  surveys: 'Surveys & Certificates',
  lsa: 'Life Saving Appliances (LSA)',
  ffe: 'Fire Fighting Equipment (FFE)',
  radio: 'Radio & GMDSS',
  machinery: 'Machinery',
  electrical: 'Electrical Systems',
  navigation: 'Navigation Equipment',
  medical: 'Medical Equipment',
  special_ops: 'Special Operations',
  crew: 'Crew Training & Certificates'
};

/**
 * Section wrapper component
 */
const SettingsSection = ({ icon: Icon, title, description, children, regulatory = false }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="bg-gray-900 px-5 py-3">
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-cyan-400" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-white">{title}</h3>
            {regulatory && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-500 text-white rounded">
                WBC3
              </span>
            )}
          </div>
          {description && (
            <p className="text-sm text-slate-300 mt-0.5">{description}</p>
          )}
        </div>
      </div>
    </div>
    <div className="p-5">
      {children}
    </div>
  </div>
);

/**
 * Toggle switch component
 */
const Toggle = ({ enabled, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    className={`
      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full
      border-2 border-transparent transition-colors duration-200 ease-in-out
      focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2
      ${enabled ? 'bg-cyan-600' : 'bg-slate-200'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    `}
  >
    <span
      className={`
        pointer-events-none inline-block h-5 w-5 transform rounded-full
        bg-white shadow ring-0 transition duration-200 ease-in-out
        ${enabled ? 'translate-x-5' : 'translate-x-0'}
      `}
    />
  </button>
);

/**
 * Number input with range
 */
const NumberInput = ({ value, onChange, min, max, unit = 'days' }) => {
  const [localValue, setLocalValue] = useState(value);

  const handleBlur = () => {
    const clamped = Math.min(max, Math.max(min, localValue));
    setLocalValue(clamped);
    if (clamped !== value) {
      onChange(clamped);
    }
  };

  // Sync when external value changes
  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <div className="flex items-center gap-3">
      <input
        type="number"
        value={localValue}
        onChange={(e) => setLocalValue(parseInt(e.target.value) || min)}
        onBlur={handleBlur}
        min={min}
        max={max}
        className="w-20 px-3 py-2 text-sm border border-slate-300 rounded-lg
          focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
      />
      <span className="text-sm text-slate-500">{unit}</span>
      <span className="text-xs text-slate-400">({min}-{max})</span>
    </div>
  );
};

/**
 * Category toggle row
 */
const CategoryToggleRow = ({ categoryKey, categoryValue, enabled, isRegulatory, onToggle }) => (
  <div className="flex items-center justify-between py-3 px-3 rounded-lg hover:bg-slate-50 border-b border-slate-100 last:border-b-0">
    <div className="flex items-center gap-3 flex-1">
      <Toggle enabled={enabled} onChange={onToggle} />
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-slate-700">
          {CATEGORY_DISPLAY_NAMES[categoryValue] || categoryKey}
        </span>
        {isRegulatory && (
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded">
            Regulatory
          </span>
        )}
      </div>
    </div>
  </div>
);

/**
 * Main MaintenanceSettingsView component
 */
const MaintenanceSettingsView = ({ onBack }) => {
  // FT-356c: register parent's onBack as the shell-level back handler.
  useSetBackHandler(onBack);

  const {
    visibleCategories,
    advanceWarningDays,
    detailedMode,
    showCompletedJobs,
    showCustomJobs,
    autoGenerateDefects,
    requireNotesForOverdue,
    hiddenRegulatoryCategories,
    toggleCategory,
    setAdvanceWarningDays,
    setDetailedMode,
    setShowCompletedJobs,
    setShowCustomJobs,
    setAutoGenerateDefects,
    setRequireNotesForOverdue,
    resetToDefaults,
    lastModified,
    modifiedBy
  } = useMaintenanceConfig();

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Show feedback message
  const showFeedback = (message, type = 'success') => {
    setFeedback({ message, type });
    setTimeout(() => setFeedback(null), 3000);
  };

  // Handle reset
  const handleReset = async () => {
    await resetToDefaults();
    setShowResetConfirm(false);
    showFeedback('Settings reset to defaults');
  };

  // Count visible categories
  const visibleCount = visibleCategories?.length || 0;
  const totalCount = Object.values(MAINTENANCE_CATEGORIES).length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* FT-356c: header raw ArrowLeft back button removed — bottom bar owns Back. */}
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-xl font-bold text-slate-900">Maintenance Settings</h1>
                <p className="text-sm text-slate-500">Configure maintenance schedule display and compliance options</p>
              </div>
            </div>

            <button
              onClick={() => setShowResetConfirm(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600
                hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Feedback Toast */}
      {feedback && (
        <div className={`
          fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg
          ${feedback.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
        `}>
          <div className="flex items-center gap-2">
            {feedback.type === 'success' ? (
              <Check className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            {feedback.message}
          </div>
        </div>
      )}

      {/* Reset Confirmation */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Reset to Defaults?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  This will reset all maintenance settings to their default values, including showing all categories and enabling compliance features. This action is logged.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
              >
                Reset Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* Audit Notice */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Audit Trail Active</p>
            <p className="mt-0.5">
              All changes are logged with your name and timestamp for SMS compliance.
            </p>
          </div>
        </div>

        {/* Hidden Regulatory Categories Warning */}
        {hiddenRegulatoryCategories?.length > 0 && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Regulatory Categories Hidden</p>
              <p className="mt-0.5">
                You have hidden {hiddenRegulatoryCategories.length} regulatory categories: {' '}
                <strong>{hiddenRegulatoryCategories.map(c => CATEGORY_DISPLAY_NAMES[c] || c).join(', ')}</strong>.
                These categories contain WBC3-mandated maintenance requirements. Hiding them may affect audit compliance.
              </p>
            </div>
          </div>
        )}

        {/* Section 1: Visible Categories */}
        <SettingsSection
          icon={Eye}
          title="Visible Categories"
          description={`${visibleCount} of ${totalCount} categories visible`}
        >
          <p className="text-sm text-slate-600 mb-4">
            Select which maintenance categories to display in the schedule. Categories marked "Regulatory" contain WBC3-mandated requirements.
          </p>
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
            {Object.entries(MAINTENANCE_CATEGORIES).map(([key, value]) => (
              <CategoryToggleRow
                key={key}
                categoryKey={key}
                categoryValue={value}
                enabled={visibleCategories?.includes(value) || false}
                isRegulatory={REGULATORY_CATEGORIES.includes(value)}
                onToggle={(enabled) => toggleCategory(value, enabled)}
              />
            ))}
          </div>
        </SettingsSection>

        {/* Section 2: Advance Warning Days */}
        <SettingsSection
          icon={Calendar}
          title="Advance Warning Days"
          description="When to show upcoming maintenance warnings"
        >
          <div>
            <p className="text-sm text-slate-600 mb-3">
              Show "Due Soon" warning when a maintenance job is due within this many days.
            </p>
            <NumberInput
              value={advanceWarningDays}
              onChange={setAdvanceWarningDays}
              min={7}
              max={90}
              unit="days"
            />
          </div>
        </SettingsSection>

        {/* Section 3: Display Options */}
        <SettingsSection
          icon={List}
          title="Display Options"
          description="Control how maintenance jobs are shown"
        >
          <div className="space-y-4">
            {/* Detailed Mode */}
            <div className="flex items-start justify-between gap-4 py-2">
              <div>
                <p className="text-sm font-medium text-slate-700">Detailed Check Mode</p>
                <p className="text-sm text-slate-500 mt-0.5">
                  Show individual equipment items within each maintenance job card.
                </p>
              </div>
              <Toggle
                enabled={detailedMode}
                onChange={setDetailedMode}
              />
            </div>

            {/* Show Completed Jobs */}
            <div className="flex items-start justify-between gap-4 py-2 border-t border-slate-100">
              <div>
                <p className="text-sm font-medium text-slate-700">Show Completed Jobs</p>
                <p className="text-sm text-slate-500 mt-0.5">
                  Display completed maintenance jobs in the list (can affect performance).
                </p>
              </div>
              <Toggle
                enabled={showCompletedJobs}
                onChange={setShowCompletedJobs}
              />
            </div>

            {/* Show Custom Jobs */}
            <div className="flex items-start justify-between gap-4 py-2 border-t border-slate-100">
              <div>
                <p className="text-sm font-medium text-slate-700">Show Custom Jobs</p>
                <p className="text-sm text-slate-500 mt-0.5">
                  Display user-created custom maintenance jobs alongside regulatory jobs.
                </p>
              </div>
              <Toggle
                enabled={showCustomJobs}
                onChange={setShowCustomJobs}
              />
            </div>
          </div>
        </SettingsSection>

        {/* Section 4: Auto Generate Defects */}
        <SettingsSection
          icon={Wrench}
          title="Auto Generate Defects"
          description="Automatically create defects when maintenance issues are found"
          regulatory={true}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Create Defects Automatically</p>
              <p className="text-sm text-slate-500 mt-1">
                When maintenance issues are found, automatically create a defect record linked to the equipment.
              </p>
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Regulatory Note:</strong> WBC3 Section 3.5.7 and Appendix 8 s.11.3 require defects to be recorded and tracked.
                  Disabling this setting may affect audit compliance.
                </p>
              </div>
            </div>
            <Toggle
              enabled={autoGenerateDefects}
              onChange={setAutoGenerateDefects}
            />
          </div>
        </SettingsSection>

        {/* Section 5: Require Notes for Overdue */}
        <SettingsSection
          icon={FileText}
          title="Require Notes for Overdue Jobs"
          description="Enforce documentation when completing overdue maintenance"
          regulatory={true}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Enforce Notes Documentation</p>
              <p className="text-sm text-slate-500 mt-1">
                When enabled, users must provide an explanation when completing maintenance jobs that are overdue.
              </p>
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Regulatory Note:</strong> WBC3 Appendix 8 s.11-12 requires documentation of maintenance deficiencies.
                  Disabling this setting may affect audit compliance.
                </p>
              </div>
            </div>
            <Toggle
              enabled={requireNotesForOverdue}
              onChange={setRequireNotesForOverdue}
            />
          </div>
        </SettingsSection>

        {/* Last Modified */}
        {lastModified && (
          <div className="text-xs text-slate-500 text-center pt-4">
            Last modified: {new Date(lastModified).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
            {modifiedBy?.name && ` by ${modifiedBy.name}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default MaintenanceSettingsView;
